/* eslint-disable no-case-declarations */
import { all, take, put, delay } from 'redux-saga/effects';
import { eventChannel, buffers } from 'redux-saga';
import ActionType from '../redux/actionType';
import {
  makeFetchFailedActionType,
  makeFetchSuccessActionType,
} from '../event/fetchActionTypeProvider';
import {
  actionConnectionOk,
  actionConnectionLost,
  actionMqttConnectOK,
  actionMqttConnectLost,
} from '../redux/action';
import { watchHTTPRequest } from './fetchHandler';
import { v4 as uuidv4 } from 'uuid';
import MqttClient from '@/fetch/mqtt';
import WebSocketClient from '@/fetch/ws';
import { store } from './store';
import {
  addGlobalEventListener,
  removeGlobalEventListener,
} from '@/utils/emit';
import { MqttTopic } from '@/utils/constant';
import { HttpStatusCode } from '@/fetch/core/constant';
import { useSelector } from 'react-redux';

enum EmitType {
  CONNECTION_OK = 1,
  CONNECTION_LOST = 2,
  CONNECTION_ERROR = 3,
  MESSAGE = 4,
  RELOGIN = 5,
}

let wsClient: any = null;
// 创建基于ws的Channel
const wsChannel = eventChannel((emitter) => {
  addGlobalEventListener('CREATE_WS_CLIENT', (data) => {
    if (
      (wsClient === null || wsClient.socket.readyState !== WebSocket.OPEN) &&
      process.env.JDX_APP_REALTIME_WSURL
    ) {
      const wsUrl =
        process.env.JDX_APP_REALTIME_WSURL + '?username=' + data.userName || '';
      wsClient = createWsClient(emitter, wsUrl);
      (window as any).wsClient = wsClient;
    }
  });

  addGlobalEventListener('CLOSE_WS_CLIENT', () => {
    wsClient.closeSocket();
    wsClient = null;
    delete (window as any).wsClient;
  });

  return () => {
    removeGlobalEventListener('CREATE_WS_CLIENT', () => {});
    removeGlobalEventListener('CLOSE_WS_CLIENT', () => {});
    console.log('Closing websocket....');
    wsClient.close();
    delete (window as any).wsClient;
  };
}, buffers.expanding(32));

const createWsClient = (emitter: any, wsUrl: string) => {
  return new WebSocketClient(wsUrl, {
    maxConnectNum: 10,
    onOpen: () => {
      console.log('connect is opend', Date.now());
      return emitter({ type: EmitType.CONNECTION_OK, data: '' });
    },
    onError: (error: any) => {
      console.log('Error: ', error);
    },
    onClose: (e: any) => {
      console.log('connect is closed', Date.now());
      emitter({ type: EmitType.CONNECTION_LOST });
    },
    onMessage: (e: any) => {
      return emitter({ type: EmitType.MESSAGE, data: e.data });
    },
  });
};

/**
 * 处理发送给websocket的数据请求
 */
function* watchWsRequest() {
  while (true) {
    const data = yield take(ActionType.SEND_SOCKET_DATA);
    while (
      wsClient === null ||
      wsChannel === null ||
      wsClient.socket.readyState !== WebSocket.OPEN
    ) {
      yield delay(500);
    }
    yield wsClient.sendMessage(data.payload);
  }
}

/**
 * 接收websocket发送来的请求，并触发相应action
 */
function* watchWsResponse() {
  while (true) {
    while (
      wsClient === null ||
      wsChannel === null ||
      wsClient.socket.readyState !== WebSocket.OPEN
    ) {
      yield delay(500);
    }
    const emitData = yield take(wsChannel);

    switch (emitData.type) {
      case EmitType.MESSAGE:
        const resp = JSON.parse(emitData.data);
        const actionType = resp.eventType;
        yield put({
          type:
            resp.code == HttpStatusCode.Success
              ? makeFetchSuccessActionType(actionType)
              : makeFetchFailedActionType(actionType),
          payload: resp,
        });
        break;
      case EmitType.CONNECTION_OK:
        yield put(actionConnectionOk());
        break;
      case EmitType.CONNECTION_LOST:
        yield put(actionConnectionLost());
        break;
    }
  }
}

// 创建基于mqtt的Channel
const mqttChannel = eventChannel((emitter) => {
  let mqttClient: any = null;
  addGlobalEventListener('CREATE_MQTT_CLIENT', ({ token }) => {
    if (mqttClient) {
      return;
    }
    mqttClient = createMqttClient(emitter, token);
    (window as any).mqttClient = mqttClient;
  });

  addGlobalEventListener('CLOSE_MQTT_CLIENT', () => {
    mqttClient?.end();
    mqttClient = null;
    delete (window as any).mqttClient;
  });

  return () => {
    removeGlobalEventListener('CREATE_MQTT_CHANNEL', () => {});
    removeGlobalEventListener('CLOSE_MQTT', () => {});
    mqttClient.end();
    delete (window as any).mqttClient;
  };
}, buffers.expanding(32));

const createMqttClient = (emitter: any, token: any) => {
  const { login, cockpitInfo } = store.getState();
  const num = Date.now() + Math.random();
  // console.log('token', token);
  console.log('clientId', `${login.user?.username}_monitor_web_${num}`);
  return new MqttClient(process.env.JDX_APP_MQTT_URL!, {
    maxConnectNum: 10,
    clean: true,
    connectTimeout: 4000,
    clientId: `${login.user?.username}_monitor_web_${num}`,
    username: login.user?.username,
    password: token,
    subscribes: [
      `${MqttTopic.ISSUE_INFO}${login.user?.username}`,
      `${MqttTopic.USER_INFO}${login.user?.username}`,
      `${MqttTopic.TEAM_INFO}${cockpitInfo.cockpitTeamNumber}`,
    ],
    will: {
      topic: `${MqttTopic.MQTT_WILL}${login.user?.username}`,
      payload: JSON.stringify({
        header: {
          requestId: Date.now(),
          clientName: `${login.user?.username}_monitor_web_${num}`,
          requestTime: Date.now(),
          needResponse: false,
        },
        data: { online: false },
      }),
      QoS: 2,
      retain: false,
    },
    events: {
      onConnect: (client) => {
        emitter({ type: EmitType.CONNECTION_OK });
        client.publish(
          MqttTopic.MQTT_WILL + login.user?.username,
          JSON.stringify({
            header: {
              requestId: Date.now(),
              clientName: `${login.user?.username}_monitor_web_${num}`,
              requestTime: Date.now(),
              needResponse: false,
            },
            data: {
              online: true,
            },
          }),
        );
        console.log('拉取mqtt数据了');
      },
      onError: (err) => {
        console.log(err);
      },
      onMessage: (topic: string, message: any, packet: any) => {
        emitter({
          type: EmitType.MESSAGE,
          ...message,
        });
      },
      onClose: () => {
        // console.log('onClose');
      },
    },
  });
};

// 监听mqtt消息
function* watchMqttResponse() {
  while (true) {
    while (mqttChannel === null) {
      yield delay(50);
    }
    let emitData = yield take(mqttChannel);
    const { type, header, data } = emitData;
    // 根据messagetype分发
    switch (emitData.type) {
      case EmitType.CONNECTION_OK:
        yield put(actionMqttConnectOK());
        break;
      case EmitType.MESSAGE:
        yield put({ type: header.messageType, payload: data });
        break;
    }
  }
}

/**
 * saga启动
 */
export default function* rootSagas() {
  yield all([
    watchHTTPRequest(),
    watchMqttResponse(),
    watchWsRequest(),
    watchWsResponse(),
  ]);
}
